import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FEEDBACK_CHAT_ENABLED } from '../../config/featureFlags';
import { useFeedbackChat } from '../../hooks/useFeedbackChat';
import FeedbackDisclaimerCard from './FeedbackDisclaimerCard';
import FeedbackThread from './FeedbackThread';
import FeedbackComposer from './FeedbackComposer';
import FeedbackSuggestedPrompts from './FeedbackSuggestedPrompts';
import FeedbackEscalateButton from './FeedbackEscalateButton';
import { MessageIcon } from '../layout/memberPrimitives';

/**
 * Ask Melio as a floating pop-up.
 *
 * Mounted once in MemberShell, so it is reachable from every member page AND
 * its state survives navigation — open the panel on /reports, wander to
 * /intelligence, and the conversation is still there. That is the main thing a
 * pop-up buys over the route: feedback usually occurs to someone *while* they
 * are doing something else, and making them leave the page to report it loses
 * both the thought and the context.
 *
 * LAYERING, against the member app's existing fixed elements:
 *   MemberRail    z-40  (fixed left, 80px)
 *   this panel    z-40  (fixed right; later in the DOM, so it paints above the
 *                        rail where they overlap — which only happens on mobile,
 *                        where the panel is deliberately full-screen)
 *   this launcher z-30  (never above the panel)
 *   EventModal    z-50  (the escalate confirmation must stay on top)
 *
 * On a phone the panel is full-screen rather than a floating card: the shell
 * already surrenders 80px to the rail, and a 295px-wide chat box is not worth
 * shipping.
 */

const PANEL_ID = 'ask-melio-panel';

export default function FeedbackLauncher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  const chat = useFeedbackChat(location.pathname);

  // Escape closes, and focus returns to the launcher rather than being dropped
  // at the top of the document.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleToggle = useCallback(() => setOpen((v) => !v), []);

  // The dedicated page mounts its own instance. Rendering the launcher there
  // too would mean two hooks, two bootstrap calls and two independent
  // conversations on the same screen.
  if (location.pathname === '/feedback') return null;

  // Build-time flag first so production never even instantiates the hook — and
  // so no bootstrap callable fires for members who cannot use this.
  if (!FEEDBACK_CHAT_ENABLED) return null;
  if (chat.loading || !chat.available) return null;

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        aria-label={open ? 'Close Ask Melio' : 'Ask Melio — questions and feedback'}
        className="fixed flex items-center gap-2.5 rounded-full transition-opacity hover:opacity-90"
        style={{
          bottom: 24,
          right: 24,
          zIndex: 30,
          backgroundColor: 'var(--melio-ink)',
          color: 'var(--melio-cream-warm)',
          padding: '11px 18px',
          boxShadow: '0 6px 20px rgba(31, 42, 30, 0.22)',
        }}
      >
        <MessageIcon size={18} />
        <span className="text-[13px] tracking-[0.02em] hidden sm:inline">
          {open ? 'Close' : 'Ask Melio'}
        </span>
      </button>

      {open && (
        <div
          id={PANEL_ID}
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="Ask Melio — questions and feedback"
          className={[
            'fixed flex flex-col overflow-hidden',
            // Phone: full screen. The shell already gives 80px to the rail, and
            // a 295px-wide chat box is not worth shipping.
            'inset-0',
            // Desktop: a floating card clear of the launcher.
            'md:inset-auto md:right-6 md:bottom-24',
            'md:w-[400px] md:h-[620px] md:max-h-[calc(100vh-140px)]',
            'md:rounded-3xl md:shadow-2xl md:border',
          ].join(' ')}
          style={{
            zIndex: 40,
            backgroundColor: 'var(--melio-cream-warm)',
            borderColor: 'var(--melio-border)',
          }}
        >
          <PanelHeader onClose={() => setOpen(false)} />

            <div className="flex-1 overflow-y-auto px-4 pb-2">
              {!chat.acknowledged && chat.bootstrap && (
                <FeedbackDisclaimerCard bootstrap={chat.bootstrap} onAcknowledge={chat.acknowledge} />
              )}

              {chat.acknowledged && (
                <>
                  {chat.bootstrap?.disclaimer?.notMonitored && (
                    <p
                      className="rounded-2xl px-3 py-2 my-3 text-[12px] leading-relaxed"
                      style={{
                        backgroundColor: 'var(--melio-cream)',
                        border: '1px solid var(--melio-border)',
                        color: 'var(--melio-terracotta-deep)',
                      }}
                    >
                      {chat.bootstrap.disclaimer.notMonitored}
                    </p>
                  )}

                  {chat.messages.length === 0 && chat.bootstrap?.suggestedPrompts && (
                    <FeedbackSuggestedPrompts
                      prompts={chat.bootstrap.suggestedPrompts}
                      onSelect={(prompt) => chat.send(prompt)}
                      disabled={chat.sendState === 'sending'}
                    />
                  )}

                  {chat.messages.length > 0 && (
                    <FeedbackThread
                      messages={chat.messages}
                      sending={chat.sendState === 'sending'}
                      onRate={chat.rate}
                      maxHeight="100%"
                    />
                  )}
                </>
              )}
            </div>

            {chat.acknowledged && (
              <div className="px-4 pb-3">
                <FeedbackComposer
                  value={chat.draft}
                  onChange={chat.setDraft}
                  onSend={() => chat.send()}
                  onRetry={chat.retry}
                  state={chat.sendState}
                  errorMessage={chat.errorMessage}
                  maxChars={chat.maxChars}
                  disabled={chat.atMessageLimit}
                  disabledReason={chat.atMessageLimit
                    ? 'This conversation has got long. Everything is saved — start a new one to add more.'
                    : null}
                />
                <div className="flex items-center justify-between gap-3 pt-1">
                  {chat.conversationId
                    ? <FeedbackEscalateButton onEscalate={chat.escalate} />
                    : <span />}
                  <button
                    type="button"
                    onClick={chat.startNewConversation}
                    className="text-[12px] underline underline-offset-4"
                    style={{ color: 'var(--melio-text-muted)' }}
                  >
                    New conversation
                  </button>
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
}

function PanelHeader({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="flex items-start justify-between px-5 py-4 shrink-0"
      style={{ borderBottom: '1px solid var(--melio-border)' }}
    >
      <div className="flex flex-col gap-1">
        <span
          className="text-[10px] uppercase tracking-[0.22em] leading-none"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          AI assistant
        </span>
        <span
          className="text-xl font-normal leading-none"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          Ask Melio
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close Ask Melio"
        className="rounded-full flex items-center justify-center"
        style={{
          width: 32,
          height: 32,
          backgroundColor: 'var(--melio-cream)',
          color: 'var(--melio-ink)',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
